import { useState } from "react";
import { useCreate } from "ra-core";
import { User, Phone, Mail, Users, Save, MapPin } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CONTACT_SOURCES } from "../constants";
import { AddressInput } from "../AddressInput";
import type { Contact } from "../../providers/fakerest/dataGenerator/types";

interface ContactCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (contact: Contact) => void;
  defaultAddress?: string;
}

export const ContactCreateDialog = ({
  open,
  onOpenChange,
  onSuccess,
  defaultAddress = "",
}: ContactCreateDialogProps) => {
  const [create, { isPending: isCreating }] = useCreate();

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState(defaultAddress);
  const [source, setSource] = useState<string>("referral");

  const resetForm = () => {
    setName("");
    setPhone("");
    setEmail("");
    setAddress(defaultAddress);
    setSource("referral");
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      return;
    }

    create(
      "contacts",
      {
        data: {
          name: name.trim(),
          phone: phone.trim(),
          email: email.trim(),
          address: address.trim(),
          source,
          job_count: 0,
          total_value: 0,
        },
      },
      {
        onSuccess: (data) => {
          onSuccess(data as Contact);
          resetForm();
          onOpenChange(false);
        },
      },
    );
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetForm();
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Create New Contact
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Name */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Name <span className="required-asterisk">*</span>
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              autoFocus
            />
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Phone
            </Label>
            <Input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
            />
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email
            </Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="john@example.com"
            />
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Address
            </Label>
            <AddressInput
              value={address}
              onChange={setAddress}
              placeholder="123 Main St, City, State 12345"
            />
          </div>

          {/* Source */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Source
            </Label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONTACT_SOURCES.map((src) => (
                  <SelectItem key={src.id} value={src.id}>
                    <span className="flex items-center gap-2">
                      <src.icon className={`h-4 w-4 ${src.color}`} />
                      {src.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isCreating || !name.trim()}>
            <Save className="h-4 w-4 mr-2" />
            {isCreating ? "Creating..." : "Create Contact"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
